<template>
    <div class="mt-4">
        <Select v-model="objectName" v-bind:options="objectNames" class="grow mt-1 w-full" option-label="name" v-on:change="setObjectName" />
    </div>
    <MContainer class="w-full mt-4 items-center justify-center upload-container">
        <div class="w-full">
            <UniversalFilePicker
                :dropArea="true"
                :selectFilesBtn="true"
                :selectFolderBtn="true"
                :accept="meshWithTextureFormats"
                :context="processingContext ? (processingContext as DocumentContext) : undefined"
                :objectName="objectName.name"
                :singleSelection="true"
                :importTmpStls="true"
                :embedded="true"
                :showTypeColumn="false"
                :hideTreeWhenNoFolders="true"
                :showAccept="true"
                @uploadFiles="onUploadFiles" />
        </div>
        <MContainer v-if="iuState === UIState.UPLOADING" class="upload-overlay items-center justify-center">
            <ProgressSpinner style="width: 150px; height: 150px" animationDuration=".75s" />
        </MContainer>
    </MContainer>
</template>
<script lang="ts" setup>
import { devLogger, MContainer, isMeshFileFormat, meshFormats, textureFormats, useAssets, success, error } from "@xlabmedical/medic-web-framework";
import { ProjectFileType } from "@xlabmedical/medic-api";
import { getDefaultName, getObjectNames } from "@xlabmedical/implant-planner";
import { DocumentContext, exportStlObjectFromWasm, UniversalFilePicker } from "@xlabmedical/medic-web-engine";
import { CustomFile, usePatientsStore } from "@/stores/PatientsStore";
import Select from "primevue/select";
import type { StlObject } from "@xlabmedical/medic-web-engine";
import { useI18n } from "vue-i18n";

enum UIState {
    INIT,
    SELECTED_OBJECT,
    UPLOADING,
}

const patientData = defineModel<{
    firstName: string | undefined;
    lastName: string;
    notes: string | undefined;
    dateOfBirth: string;
    extra: {
        patientName: string | undefined;
        study_instance_UID: string;
    };
}>("patientData", { required: false });
const props = defineProps<{
    patientName: string;
    createPatient: () => Promise<void>;
}>();

const emits = defineEmits<{ (e: "done", value: string): void }>();

const { t } = useI18n();

const iuState = ref<UIState>(UIState.INIT);
const serverApi = useServerApi();
const assetApi = useAssets();

const patientStore = usePatientsStore();

const files = ref<File[]>([]);
const selectedMeshFile = ref<File | null>(null);
const importedStl = ref<StlObject | null>(null);
const processingContext = ref(new DocumentContext(assetApi.getAssetUrl("")));

const uploadProgress = ref(0);
const throttleUpdate = useThrottleFn((progress: number) => {
    uploadProgress.value = Math.ceil(progress * 100) - 1;
}, 250);

const objectNames = getObjectNames();
const objectName = ref(getDefaultName());
const meshWithTextureFormats = `${meshFormats},${textureFormats}`;

onMounted(async () => {
    await processingContext.value.getEngineInstance();
});

const getBaseName = (name: string) => {
    return name.replace(/\.[^/.]+$/, "");
};

const importObject = async () => {
    iuState.value = UIState.UPLOADING;
    if (patientData.value != undefined) {
        if (patientData.value.extra.study_instance_UID == "") {
            await props.createPatient();
        }
    }
    const file = selectedMeshFile.value;
    const stl = importedStl.value;
    if (!file || !stl) {
        iuState.value = UIState.INIT;
        error(t("web-rg.noMeshSelected"), t("web-rg.uploadFailed"));
        return;
    }
    const ext = file.name.toLowerCase().split(".").pop();
    const format = ext === "stl" ? "stl" : "ply";
    const baseName = getBaseName(file.name);

    try {
        const { meshFile, textureFile, unlinkCb } = await exportStlObjectFromWasm(processingContext.value as DocumentContext, stl as StlObject, baseName, format);
        await takeAndUploadScreenshot(stl as StlObject, meshFile.name);
        await uploadStlObject(meshFile);
        if (textureFile) {
            await uploadTextureFile(textureFile);
        }
        try {
            unlinkCb();
        } catch {
            // no texture exported
        }

        if (processingContext.value && importedStl.value) {
            processingContext.value.objects.stls.delete(importedStl.value.id);
            importedStl.value.delete();
        }

        success(t("web-rg.uploadSuccessWithName", { filename: file.name }), t("web-rg.uploadComplete"));

        await new Promise((resolve) => setTimeout(resolve, 1200)); // gap between WS sent to update UI

        iuState.value = UIState.SELECTED_OBJECT;
        emits("done", patientData.value ? patientData.value.extra.study_instance_UID : props.patientName);
    } catch (err) {
        iuState.value = UIState.INIT;
        error(t("web-rg.uploadFailed"));
    }
};

const takeAndUploadScreenshot = async (stlObject: StlObject, fileName: string) => {
    const screenshot = await stlObject.getScreenshot({
        width: 2560,
        height: 1440,
        background: [30, 30, 30, 0],
        orientation: "front",
    });
    if (screenshot) {
        try {
            const base64Response = await fetch(screenshot);
            const blob = await base64Response.blob();
            // upload base64 stl screenshot with the same filename as stl
            await serverApi.uploadFile(
                patientData.value ? patientData.value.extra.study_instance_UID : props.patientName,
                "[" + objectName.value.name + "]" + fileName + ".thumb",
                ProjectFileType.STL_Preview,
                blob,
            );
        } catch (e) {
            error(e);
        }
    }
};

const uploadTextureFile = async (file: File) => {
    await patientStore.uploadFile(
        patientData.value ? patientData.value.extra.study_instance_UID : props.patientName,
        "[" + objectName.value.name + "]" + file.name,
        ProjectFileType.STL_Texture,
        file as CustomFile,
    );
};

const uploadStlObject = async (file: File) => {
    await patientStore.uploadFile(
        patientData.value ? patientData.value.extra.study_instance_UID : props.patientName,
        "[" + objectName.value.name + "]" + file.name,
        ProjectFileType.STL,
        file as CustomFile,
        (progress: number) => {
            throttleUpdate(progress);
        },
    );
};

const onUploadFiles = (selection: File[], imported?: StlObject[]) => {
    iuState.value = UIState.SELECTED_OBJECT;
    files.value = selection;
    selectedMeshFile.value = selection.find((f) => isMeshFileFormat(f.name)) ?? null;
    importedStl.value = imported && imported.length ? toRaw(imported[0]) : null;
    importObject();
};

const setObjectName = () => {
    devLogger.debug("setObjectName not yet implemented");
};

onUnmounted(() => {
    if (processingContext.value) {
        processingContext.value.cleanup();
        processingContext.value = null;
    }
});
</script>

<style scoped>
.upload-container {
    position: relative;
}

.upload-overlay {
    position: absolute;
    inset: 0;
    background: rgba(100, 100, 100, 0.4);
    z-index: 2;
    border-radius: 16px;
}
</style>
